import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraActionList, SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraButton, SpecraIconButton, SpecraLink, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraPopover, SpecraRestrictedChip } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { X } from 'lucide-react';
import { Fragment, useState } from 'react';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { RfiEntityConfirm } from './RfiEntityConfirm';
import { RfiSignalComposer } from './RfiSignalComposer';
import { tokenizeSignalBody } from './rfiHomeModel';
import { useConfirmRfiEntity, useRetractRfiSignal } from './useRfiWorkspaceHome';
const T = Testids.rfiWorkspace;
let warnedMissing = false;
/** A pending placeholder: "{observed} · confirm" opening REG-54's prompt. */ function PendingMention({ link, entity, rfiResponseProjectId }) {
    const [anchor, setAnchor] = useState(null);
    const [open, setOpen] = useState(false);
    const confirm = useConfirmRfiEntity(rfiResponseProjectId);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                className: "align-baseline",
                onClick: (e)=>{
                    setAnchor(e.currentTarget);
                    setOpen(true);
                },
                size: "inline",
                variant: "link",
                children: /*#__PURE__*/ _jsxs(SpecraBadge, {
                    variant: "warning",
                    children: [
                        link.pendingLabel || 'someone',
                        " · confirm"
                    ]
                })
            }),
            entity ? /*#__PURE__*/ _jsx(SpecraPopover, {
                anchor: anchor,
                "aria-label": "Confirm the mention",
                onOpenChange: setOpen,
                open: open,
                children: /*#__PURE__*/ _jsx(RfiEntityConfirm, {
                    busy: confirm.isPending,
                    entity: entity,
                    onDecide: (d)=>confirm.mutate({
                            linkId: entity.linkId,
                            decision: d.decision,
                            registryId: d.decision === 'link' ? d.registryId : ''
                        }, {
                            onSuccess: ()=>setOpen(false)
                        })
                })
            }) : null
        ]
    });
}
/**
 * Renders a "{{n}}" signal body: linked → SpecraLink, pending → a confirm
 * badge, restricted → SpecraRestrictedChip; an unknown placeholder renders as
 * nothing (logged once).
 */ export function renderSignalBody(body, links, opts = {}) {
    return tokenizeSignalBody(body, links).map((t, i)=>{
        const key = `${t.kind}-${i}`;
        if (t.kind === 'text') return /*#__PURE__*/ _jsx(Fragment, {
            children: t.text
        }, key);
        if (t.kind === 'missing') {
            if (!warnedMissing) {
                warnedMissing = true;
                console.warn(`RFI signal placeholder {{${t.ordinal}}} has no link`);
            }
            return null;
        }
        const l = t.link;
        if (l.state === 'pending') {
            const entity = opts.entities?.find((e)=>e.candidateId === l.candidateId);
            return /*#__PURE__*/ _jsx(PendingMention, {
                entity: entity,
                link: l,
                rfiResponseProjectId: opts.rfiResponseProjectId ?? ''
            }, key);
        }
        if (l.state === 'restricted') return /*#__PURE__*/ _jsx(SpecraRestrictedChip, {
            label: "Restricted record"
        }, key);
        return l.href ? /*#__PURE__*/ _jsx(SpecraLink, {
            href: l.href,
            renderLink: renderRouterLink,
            children: l.label
        }, key) : /*#__PURE__*/ _jsx(Fragment, {
            children: l.label
        }, key);
    });
}
/** "What this RFI tells us · posted to the pursuit thread". */ export function RfiSignalsList({ rfiResponseProjectId, signals, entities, linkedPursuits, canEdit }) {
    const [posting, setPosting] = useState(false);
    const [retracting, setRetracting] = useState(null);
    const retract = useRetractRfiSignal(rfiResponseProjectId);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-2)]",
        "data-testid": T.signals,
        children: [
            signals.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "body-ui",
                tone: "muted",
                children: "Nothing posted yet. Signals land in the pursuit thread."
            }) : /*#__PURE__*/ _jsx(SpecraActionList, {
                getItemTestId: ()=>T.signalRow,
                items: signals.map((s)=>({
                        id: s.id,
                        label: /*#__PURE__*/ _jsx("span", {
                            children: renderSignalBody(s.body, s.links, {
                                entities,
                                rfiResponseProjectId
                            })
                        }),
                        meta: s.statusLabel || undefined,
                        actions: s.reviewHref || canEdit && s.canRetract ? /*#__PURE__*/ _jsxs("span", {
                            className: "inline-flex items-center gap-[var(--space-1-5)]",
                            children: [
                                s.reviewHref ? /*#__PURE__*/ _jsx(SpecraLink, {
                                    href: s.reviewHref,
                                    renderLink: renderRouterLink,
                                    children: "Review →"
                                }) : null,
                                canEdit && s.canRetract ? /*#__PURE__*/ _jsx(SpecraIconButton, {
                                    "data-testid": T.signalRetract,
                                    label: "Retract this signal",
                                    onClick: ()=>setRetracting(s.id),
                                    size: "sm",
                                    variant: "ghost",
                                    children: /*#__PURE__*/ _jsx(X, {})
                                }) : null
                            ]
                        }) : undefined
                    }))
            }),
            canEdit ? /*#__PURE__*/ _jsx("div", {
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": T.postSignal,
                    onClick: ()=>setPosting(true),
                    size: "sm",
                    variant: "ghost",
                    children: "Post a signal"
                })
            }) : null,
            /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                onOpenChange: setPosting,
                open: posting,
                size: "md",
                title: "Post a signal",
                children: /*#__PURE__*/ _jsx(RfiSignalComposer, {
                    linkedPursuits: linkedPursuits,
                    onPosted: ()=>setPosting(false),
                    rfiResponseProjectId: rfiResponseProjectId
                })
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                onOpenChange: (o)=>!o && setRetracting(null),
                open: retracting !== null,
                size: "sm",
                title: "Retract this signal?",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col gap-[var(--space-3)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            size: "body-ui",
                            children: "It disappears from the pursuit thread. It cannot be edited."
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-end gap-[var(--space-2)]",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    onClick: ()=>setRetracting(null),
                                    size: "sm",
                                    variant: "secondary",
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    disabled: retract.isPending,
                                    onClick: ()=>retracting && retract.mutate(retracting, {
                                            onSuccess: ()=>setRetracting(null)
                                        }),
                                    size: "sm",
                                    variant: "destructive",
                                    children: "Retract"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
